import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import blipService from '../services/blipService';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiKey, FiSave, FiCheckCircle, FiInfo, FiActivity, FiAlertCircle } = FiIcons;

function Settings() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [saved, setSaved] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // { success: bool, message: string }
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('blip_token');
    if (storedToken) setToken(storedToken);
  }, []);

  const handleSave = () => {
    localStorage.setItem('blip_token', token);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const runDiagnostics = async () => {
    setIsTesting(true);
    setTestStatus(null);
    const result = await blipService.testConnection();
    setTestStatus(result);
    setIsTesting(false);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="min-h-screen p-4 pt-24 bg-safari-50"
    >
      <div className="max-w-xl mx-auto">
        <button 
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 bg-white px-6 py-3 rounded-2xl shadow-lg border-2 border-safari-200 font-kid text-jungle-700 hover:bg-safari-100 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} /> Back
        </button>

        <div className="bg-white rounded-[3rem] p-10 shadow-2xl border-4 border-safari-200">
          <div className="text-center mb-10">
            <div className="text-6xl mb-4">⚙️</div>
            <h1 className="text-4xl font-kid text-jungle-700">Settings</h1>
            <p className="font-comic text-jungle-600">Prepare your tools for the Reading Quest!</p>
          </div>

          <div className="space-y-6">
            <div className="bg-blue-50 rounded-3xl p-6 border-2 border-blue-100">
              <label className="block font-kid text-blue-900 mb-3 flex items-center gap-2 text-xl">
                <SafeIcon icon={FiKey} className="text-blue-500" />
                Blip AI Token
              </label>
              <input 
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste Bearer token..."
                className="w-full p-4 bg-white border-4 border-blue-200 rounded-2xl focus:outline-none focus:border-blue-400 font-mono text-sm shadow-inner mb-4"
              />
              
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  className={`flex-1 p-4 rounded-xl font-kid text-lg shadow-md flex items-center justify-center gap-2 transition-all ${
                    saved ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  <SafeIcon icon={saved ? FiCheckCircle : FiSave} />
                  {saved ? 'Saved!' : 'Save Token'}
                </button>
                
                <button
                  onClick={runDiagnostics}
                  disabled={isTesting || !token}
                  className="flex-1 p-4 bg-purple-500 text-white rounded-xl font-kid text-lg shadow-md hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiActivity} className={isTesting ? 'animate-spin' : ''} />
                  {isTesting ? 'Testing...' : 'Test Link'}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {testStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`p-4 rounded-2xl border-2 flex items-start gap-3 font-comic text-sm ${
                    testStatus.success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                  }`}
                >
                  <SafeIcon icon={testStatus.success ? FiCheckCircle : FiAlertCircle} className="mt-1 flex-shrink-0" />
                  <p>{testStatus.message}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="bg-gray-50 rounded-2xl p-4 border-2 border-gray-100">
              <div className="flex items-center gap-2 text-jungle-700 font-kid mb-2">
                <SafeIcon icon={FiInfo} />
                <span>CORS Requirement</span>
              </div>
              <p className="font-comic text-xs text-gray-500 leading-relaxed">
                If "Test Link" fails with a red message about CORS, you must add 
                <code className="bg-gray-200 px-1 rounded mx-1">https://silly-starburst-3d11ff.netlify.app</code> 
                to the <b>CORS</b> section of your Azure App Service in the Azure Portal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Settings;